import { FC, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { DataGrid, GridPaginationModel, GridSortDirection, GridSortModel, useGridApiRef } from '@mui/x-data-grid';
import type { GridColDef } from "@mui/x-data-grid/models/colDef/gridColDef";
import { ROUTES } from "routes";
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
	clearStreetTypesData,
	getStreetsTypes,
	getStreetsTypesSelector,
	getStreetTypesIsLoadingSelector,
	updateStreetType,
} from 'store/streets-types';
import { IQuery, IStreetsDetails } from 'types';
import { PAGINATION_LIMIT } from 'utils/constants';
import { DATE_FORMAT, SORT_DIR, STREETS_FIELDS_NAME } from 'utils/enums';
import { formatQueryString, parseQueryString } from 'utils/helpers';
import { formatDateView  } from "utils/helpers/date";
import { getTableSetting } from "utils/helpers/table-setting";
import { usePermissions } from 'utils/hooks/use-permissions';
import { useColumns } from 'utils/hooks/use-selection-fields';
import { COLUMNS_CONFIG, withPermissions } from './config';
import s from './index.module.scss';

export const StreetTypesPage: FC = () => {
	const { search } = useLocation();
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const checkRights = usePermissions()
	const apiRef = useGridApiRef();
	const [columnWidths, setColumnWidths] = useState({});

	const streetsTypes = useAppSelector(getStreetsTypesSelector);
	const isLoading = useAppSelector(getStreetTypesIsLoadingSelector);

	const query = useMemo(() => parseQueryString<IQuery>(search, { parseBooleans: true, parseNumbers: true }), [search]);

	const requestBody = useMemo(
		() => ({
			offset: (query.page || 0) * (query.pageSize || 0),
			limit: query.pageSize || PAGINATION_LIMIT.SMALL,
			orderBy: [
				{
					field: query.field || STREETS_FIELDS_NAME.NAME,
					direction: query.sort || SORT_DIR.ASC,
				},
			],
			searchBy: {},
			deviceFP: "6037e65eefbdasdfadsfasdf3"
		}),
		[query]
	);

	useEffect(() => {
		dispatch(getStreetsTypes(requestBody));
	}, [requestBody, dispatch]);

	const handleChangePage = ({ page, pageSize }: GridPaginationModel): void => {
		navigate(
			{
				search: formatQueryString({
					...query,
					page,
					pageSize,
				}),
			},
			{ replace: true }
		);
	};

	const handleCellEdit = (newRow: IStreetsDetails, _: IStreetsDetails): IStreetsDetails => {
		dispatch(updateStreetType(newRow));
		return newRow;
	};

	useEffect(() => {
		return () => {
			dispatch(clearStreetTypesData());
		};
	}, [dispatch]);

	const handleColumnResize = (params: any) => {
		const newWidths = {
			...columnWidths,
			[params.colDef.field]: params.width,
		};
		setColumnWidths(newWidths);
		localStorage.setItem(ROUTES.streetsType, JSON.stringify(newWidths));
	};

	useEffect(() => {
		setColumnWidths(getTableSetting(ROUTES.streetsType));
	}, [requestBody]);

	COLUMNS_CONFIG.forEach((item: GridColDef) => {
		// @ts-ignore
		if (columnWidths && columnWidths[item.field]) {
			// @ts-ignore
			item.width = columnWidths[item.field];
			item.flex = undefined;
		}
	});

	const handleSortField = (model: GridSortModel): void => {
		const { field, sort } = model[0] || {};
		navigate(
			{
				search: formatQueryString({
					...query,
					field,
					sort,
					page: 0,
				}),
			},
			{ replace: true }
		);
	};

	const modifiedStreetsType = useMemo(
		() =>
			streetsTypes?.results.map((streetType) => ({
				...streetType,
				createdAt: formatDateView(streetType.createdAt, DATE_FORMAT.DATE_FULL),
				updatedAt: formatDateView(streetType.createdAt, DATE_FORMAT.DATE_FULL),
			})),
		[streetsTypes]
	);


	return (
		<Box className={s.tableWrapper}>
			<DataGrid
				onColumnResize={handleColumnResize}
				apiRef={apiRef}
				disableColumnFilter
				disableRowSelectionOnClick
				rows={modifiedStreetsType || []}
				columns={useColumns(withPermissions(checkRights))}
				rowCount={streetsTypes?.total || 0}
				loading={isLoading}
				initialState={{
					pagination: {
						paginationModel: { page: query.page || 0, pageSize: query.pageSize || PAGINATION_LIMIT.SMALL },
					},
					sorting: {
						sortModel: [{ field: query.field || STREETS_FIELDS_NAME.CREATE_AT, sort: query.sort as GridSortDirection }],
					},
				}}
				slotProps={{
					pagination: {
						labelRowsPerPage: 'Показувати :',
						page: query.page || 0,
						rowsPerPage: query.pageSize || PAGINATION_LIMIT.SMALL,
					},
				}}
				processRowUpdate={handleCellEdit}
				paginationMode="server"
				sortingMode="server"
				onSortModelChange={handleSortField}
				onPaginationModelChange={handleChangePage}
				className={s.table}
				pageSizeOptions={[PAGINATION_LIMIT.SMALL, PAGINATION_LIMIT.MIDDLE, PAGINATION_LIMIT.BIG]}
			/>
		</Box>
	);
};
